// Vendor imports
const _ = require('lodash')

const CLOUDINARY_DOMAIN = 'https://img.mix.com'
// Whitelist of supported domains for cloudinary fetch transform
const ELIGIBLE_DOMAINS = [
  'https://d2xuupin39grhy.cloudfront.net',
  'https://d9rgmw4vu05b1.cloudfront.net',
  'https://images.mix.com',
  'https://staging-getmixapp-com-app-storage.s3.amazonaws.com',
  'https://surveyor-stg-mix-com-storage.s3.amazonaws.com',
  'https://assets.mix.com',
  'https://assets-stg.mix.com'
]

function isCloudinaryImageUrl(imageUrl) {
  return _.startsWith(imageUrl, CLOUDINARY_DOMAIN)
}

function isEligibleImageUrl(imageUrl) {
  if (!imageUrl || isCloudinaryImageUrl(imageUrl)) {
    return false
  }
  for (let eligibleDomain of ELIGIBLE_DOMAINS) {
    if (_.startsWith(imageUrl, eligibleDomain)) {
      return true
    }
  }
  return false
}

// mix-model helpers

const MIX_COVER_IMAGE_DIMENSION_BY_SIZE = {
  // Cover images are square; only one dimension is needed
  small: 200,
  medium: 400,
  large: 800
}

const MIX_HEADER_IMAGE_DIMENSIONS_BY_SIZE = {
  small: [1500, 384],
  medium: [3000, 384],
  large: [3000, 768]
}

const convertMixModel = _.flow([
  convertMixModelCoverImageUrl,
  convertMixModelHeaderImageUrl
])

function convertMixModels(mixes) {
  return mixes.map(convertMixModel)
}

function convertMixModelCoverImageUrl(mix) {
  // Expects a model object from app/models/user-model
  const coverImageUrl = getMixCoverImageUrl(mix)
  if (isEligibleImageUrl(coverImageUrl) && !mix['cover_image_urls']) {
    const sizedImageUrls = {}
    _.each(MIX_COVER_IMAGE_DIMENSION_BY_SIZE, (dimension, size) => {
      sizedImageUrls[size] = generateMixCoverImageUrl(coverImageUrl, dimension)
    })
    mix['cover_image_urls'] = sizedImageUrls
  }
  return mix
}

function convertMixModelHeaderImageUrl(mix) {
  // Expects a model object from app/models/user-model
  const headerImageUrl = mix['header_image_url']
  if (isEligibleImageUrl(headerImageUrl) && !mix['header_image_urls']) {
    const sizedImageUrls = {}
    _.each(MIX_HEADER_IMAGE_DIMENSIONS_BY_SIZE, (dimensions, size) => {
      sizedImageUrls[size] = generateMixHeaderImageUrl(headerImageUrl, ...dimensions)
    })
    mix['header_image_urls'] = sizedImageUrls
    // Keep `header_image_url` key around for compatibility with older clients
    // https://app.asana.com/0/877570077568106/898068353404783/f
    mix['header_image_url'] = mix['header_image_urls'].large
  }
  return mix
}

function generateMixCoverImageUrl(imageUrl, dimension) {
  const fetchFormat = imageUrl.endsWith('.gif') ? 'f_gif' : 'f_jpg'
  return [
    CLOUDINARY_DOMAIN,
    'image/fetch',
    `c_crop,ar_1`,
    `c_lfill,w_${dimension},h_${dimension},q_auto,${fetchFormat}`,
    imageUrl
  ].join('/')
}

function generateMixHeaderImageUrl(imageUrl, width, height) {
  return [
    CLOUDINARY_DOMAIN,
    'image/fetch',
    `c_lfill,h_${height},w_${width}`,
    `c_crop,h_${height},w_iw,q_auto,f_jpg`,
    imageUrl
  ].join('/')
}

function getMixCoverImageUrl(mix) {
  const coverImageUrl = mix['cover_image_url']
  if (coverImageUrl) {
    return coverImageUrl
  }
  for (let imageUrl of mix.images || []) {
    if (isEligibleImageUrl(imageUrl)) {
      return imageUrl
    }
  }
}

// user-model helpers

const USER_AVATAR_IMAGE_ASPECT_RATIO = 1

const USER_HEADER_IMAGE_ASPECT_RATIO = 3.703
const USER_HEADER_IMAGE_WIDTHS_BY_SIZE = {
  small: 918,
  medium: 1500,
  large: 3000
}

// Singular form of convertUserModels
function convertUserModel(user) {
  // Expects a model object from app/models/user-model
  const photoImageUrl = user['photo_url']
  if (isEligibleImageUrl(photoImageUrl)) {
    user['photo_url'] = generateUserAvatarImageUrl(photoImageUrl, 400)
  }
  const headerImageUrl = user['header_image_url']
  if (isEligibleImageUrl(headerImageUrl) && !user['header_image_urls']) {
    const sizedImageUrls = {}
    _.each(USER_HEADER_IMAGE_WIDTHS_BY_SIZE, (width, size) => {
      sizedImageUrls[size] = generateUserHeaderImageUrl(headerImageUrl, width)
    })
    user['header_image_urls'] = sizedImageUrls
    // Keep `header_image_url` key around for compatibility with older clients
    // https://app.asana.com/0/877570077568106/898068353404783/f
    user['header_image_url'] = user['header_image_urls'].large
  }
  return user
}

// Aggregate form of convertUserModel
function convertUserModels(users) {
  // Expects an Array of model objects from app/models/user-model
  return users.map(convertUserModel)
}

function generateUserAvatarImageUrl(imageUrl, width) {
  const height = Math.round(width / USER_AVATAR_IMAGE_ASPECT_RATIO)
  return [
    CLOUDINARY_DOMAIN,
    'image/fetch',
    `c_crop,ar_${USER_AVATAR_IMAGE_ASPECT_RATIO}`,
    `c_lfill,w_${width},h_${height},q_auto,f_jpg`,
    imageUrl
  ].join('/')
}

function generateUserHeaderImageUrl(imageUrl, width) {
  const height = Math.round(width / USER_HEADER_IMAGE_ASPECT_RATIO)
  return [
    CLOUDINARY_DOMAIN,
    'image/fetch',
    `c_crop,ar_${USER_HEADER_IMAGE_ASPECT_RATIO}`,
    `c_lfill,w_${width},h_${height},q_auto,f_jpg`,
    imageUrl
  ].join('/')
}


// article helpers

const DEFAULT_ARTICLE_WIDTH = 1500
const DEFAULT_ARTICLE_HEIGHT = 1500

function generateArticleImageUrl(imageUrl) {
  if (isEligibleImageUrl(imageUrl)) {
    const fetchFormat = imageUrl.endsWith('.gif') ? 'f_gif' : 'f_jpg'
    return [
      CLOUDINARY_DOMAIN,
      'image/fetch',
      `c_limit,q_auto,${fetchFormat},w_${DEFAULT_ARTICLE_WIDTH},h_${DEFAULT_ARTICLE_HEIGHT}`,
      imageUrl
    ].join('/')
  }
  return imageUrl
}

module.exports = {
  isCloudinaryImageUrl,
  isEligibleImageUrl,
  // mix-model exports
  convertMixModel,
  convertMixModelCoverImageUrl,
  convertMixModelHeaderImageUrl,
  convertMixModels,
  generateMixCoverImageUrl,
  generateMixHeaderImageUrl,
  getMixCoverImageUrl,
  // user-model exports
  convertUserModel,
  convertUserModels,
  generateUserHeaderImageUrl,
  generateArticleImageUrl,
  generateUserAvatarImageUrl
}
